import { track } from '@amplitude/analytics-browser'
import { sanitizePaymentTelemetry } from 'utils/payment-return-url'

import './cf-tl-event'
import { isAmplitudeTrackingAllowed } from './tracking-state'

interface QueuedEvent {
  eventName: string
  eventProperties?: Record<string, unknown>
  callback?: () => void
  timestamp: number
}

const eventQueue: QueuedEvent[] = []

// Preserve the original event time and use the same session as live Tag Loader events.
const sendQueuedEvent = ({ eventName, eventProperties, callback, timestamp }: QueuedEvent) => {
  const properties = { ...eventProperties, queued_event: true }
  const ampInstance = window?.cfTLEvent?.ampInstance
  if (ampInstance) {
    if (typeof ampInstance.logEventWithTimestamp === 'function') {
      ampInstance.logEventWithTimestamp(eventName, properties, timestamp, callback)
    } else {
      ampInstance.logEvent(eventName, properties, callback)
    }
    return
  }

  track(eventName, properties, { time: timestamp })
  callback?.()
}

export const processEventQueue = () => {
  while (isAmplitudeTrackingAllowed() && eventQueue.length > 0) {
    const queuedEvent = eventQueue.shift()
    if (queuedEvent) {
      sendQueuedEvent(queuedEvent)
    }
  }
}

export const enqueueEvent = (
  eventName: string,
  eventProperties?: Record<string, unknown>,
  callback?: () => void,
) => {
  const pendingConsent = !isAmplitudeTrackingAllowed()
  eventQueue.push({
    eventName,
    eventProperties: sanitizePaymentTelemetry(eventProperties),
    callback: pendingConsent ? undefined : callback,
    timestamp: Date.now(),
  })
  if (pendingConsent) {
    callback?.()
  }
}

export const getEventQueue = (): QueuedEvent[] => {
  return eventQueue
}

export const clearEventQueue = () => {
  const discardedEvents = eventQueue.splice(0)
  for (const event of discardedEvents) {
    event.callback?.()
  }
}

// Poll interval (ms) while waiting for Tag Loader to attach
const TL_POLL_INTERVAL_MS = 100

// Absolute ceiling (ms) before draining regardless of Tag Loader state.
// Only ever hit if window 'load' never fires (hung iframe, etc.).
const DRAIN_CEILING_MS = 15_000

/**
 * Schedule a drain of any queued Amplitude events. Prefers routing through
 * `window.cfTLEvent.ampInstance` so queued events share the same session_id
 * as live events (see GROW-2704). Falls back to `track()` once `window.load`
 * fires without Tag Loader attaching, so events are not lost in environments
 * where Tag Loader is never loaded (e.g., Playwright runs).
 */
const canDrainQueue = () => isAmplitudeTrackingAllowed() && eventQueue.length > 0

export const scheduleQueueDrain = () => {
  if (!canDrainQueue()) {
    return
  }
  if (typeof window === 'undefined') {
    return
  }

  // Fast path: TL already attached.
  if (window.cfTLEvent?.ampInstance) {
    processEventQueue()
    return
  }

  let drained = false
  const drainOnce = () => {
    if (drained) {
      return
    }
    drained = true
    processEventQueue()
  }

  const pollId = window.setInterval(() => {
    if (typeof window === 'undefined') {
      return
    }
    if (drained || window.cfTLEvent?.ampInstance) {
      window.clearInterval(pollId)
      drainOnce()
    }
  }, TL_POLL_INTERVAL_MS)

  const onLoadFallback = () => {
    if (typeof window === 'undefined') {
      return
    }
    window.clearInterval(pollId)
    drainOnce()
  }

  if (document.readyState === 'complete') {
    // Page already loaded — schedule one tick later so a very-recently-attached
    // TL has a chance to register before we give up on it.
    window.setTimeout(onLoadFallback, 0)
  } else {
    window.addEventListener('load', onLoadFallback, { once: true })
  }

  // Absolute ceiling for the rare case 'load' never fires.
  window.setTimeout(onLoadFallback, DRAIN_CEILING_MS)
}
